'use client';

import { useState, useEffect } from 'react';
import QuoteFormSteps from './QuoteFormSteps';
import ContactOptions from './ContactOptions';
import TrustIndicators from './TrustIndicators';
import QuoteTestimonials from './QuoteTestimonials';
import Icon from '@/components/ui/AppIcon';
import { analytics } from '@/lib/analytics';

interface ServiceSpecificAnswers {
  // Window Cleaning
  windowType?: string;
  skylights?: string;
  windowStories?: string;
  
  // Power Washing
  powerWashAreas?: string[];
  powerWashOther?: string;
  mossBuildup?: string;
  
  // Roof Cleaning
  roofType?: string;
  roofPitch?: string;
  roofStories?: string;
  
  // Gutter Cleaning
  gutterRoofPitch?: string;
  gutterStories?: string;
}

interface FormData {
  services: string[];
  propertyType: string;
  propertySize: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferredDate: string;
  preferredTime: string;
  communicationPreference: string;
  additionalNotes: string;
  photos: File[];
  promoCode?: string;
  scheduledDate?: string;
  scheduledTime?: string;
  // Added missing fields
  serviceAnswers: Record<string, ServiceSpecificAnswers>;
  homeSize: string;
  homeType: string;
  stories: string;
}

const GetQuoteInteractive = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState<FormData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // Track quote form start on component mount
  useEffect(() => {
    analytics.trackQuoteStart();
  }, []);

  const handleFormSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      // Calculate estimated amount using the same pricing engine as the form
      const calculateServicePrice = (serviceId: string): { min: number; max: number } | null => {
        const answers = data.serviceAnswers[serviceId] || {};
        const homeSize = data.homeSize;

        // Home size increment mapping (0-5 for 6 categories)
        const homeSizeIncrements: { [key: string]: number } = {
          'size-2000-under': 0,
          'size-2001-3000': 1,
          'size-3001-4000': 2,
          'size-4001-5000': 3,
          'size-5001-6000': 4,
          'size-6000-plus': 5,
        };

        const increment = homeSizeIncrements[homeSize] || 0;

        if (serviceId === 'window') {
          // Window Cleaning: Base $150 + $20 per increment
          let basePrice = 150 + (increment * 20);

          // Interior vs Exterior multiplier
          if (answers.windowType === 'Interior & exterior') {
            basePrice = basePrice * 2;
          }

          // Story multiplier
          const stories = parseInt(data.stories) || 1;
          if (stories === 2) basePrice = Math.round(basePrice * 1.1);
          if (stories >= 3) basePrice = Math.round(basePrice * 1.21);

          // Skylights: $10 per skylight
          let skylightCost = 0;
          if (answers.skylights === '1–2') skylightCost = 15;
          if (answers.skylights === '3–5') skylightCost = 40;
          if (answers.skylights === '6+') skylightCost = 70;

          let total = basePrice + skylightCost;
          return { min: total, max: Math.round(total * 1.2) };
        }

        if (serviceId === 'powerwashing') {
          // Power Washing: Driveway $250 + $25/increment, Walkways $125 + $25/increment, Patio $50 + $20/increment
          const areas = answers.powerWashAreas || [];
          let total = 0;

          if (areas.includes('Driveway')) {
            total += 250 + (increment * 25);
          }
          if (areas.includes('Walkways')) {
            total += 125 + (increment * 25);
          }
          if (areas.includes('Patio / deck')) {
            total += 50 + (increment * 20);
          }
          if (areas.includes('Stairs')) {
            total += 125 + (increment * 25);
          }

          // Moss buildup multiplier
          if (answers.mossBuildup === 'Yes') {
            total = Math.round(total * 1.25);
          }

          if (total === 0) return null;
          return { min: total, max: Math.round(total * 1.2) };
        }

        if (serviceId === 'gutter') {
          // Gutter Cleaning: Base $150 + $20 per increment
          let basePrice = 150 + (increment * 20);

          // Roof pitch multipliers
          if (answers.gutterRoofPitch === 'Moderately steep') {
            basePrice = Math.round(basePrice * 1.15);
          } else if (answers.gutterRoofPitch === 'Steep') {
            basePrice = Math.round(basePrice * 1.25);
          }

          // Story multiplier
          const stories = parseInt(answers.gutterStories) || 1;
          if (stories === 2) basePrice = Math.round(basePrice * 1.1);
          if (stories >= 3) basePrice = Math.round(basePrice * 1.21);

          return { min: basePrice, max: Math.round(basePrice * 1.2) };
        }

        if (serviceId === 'roof') {
          // Roof Cleaning: Base $450 + $75 per increment
          let basePrice = 450 + (increment * 75);

          // Roof material multipliers
          if (answers.roofType === 'Tile') {
            basePrice = Math.round(basePrice * 1.35);
          } else if (answers.roofType === 'Metal' || answers.roofType === 'Wood') {
            basePrice = Math.round(basePrice * 1.30);
          }

          // Story multiplier
          const stories = parseInt(answers.roofStories) || 1;
          if (stories === 2) basePrice = Math.round(basePrice * 1.1);
          if (stories >= 3) basePrice = Math.round(basePrice * 1.21);

          return { min: basePrice, max: Math.round(basePrice * 1.2) };
        }

        if (serviceId === 'house-washing') {
          // Exterior Siding: Base $400 + $40 per increment
          let basePrice = 400 + (increment * 40);
          return { min: basePrice, max: Math.round(basePrice * 1.2) };
        }

        if (serviceId === 'christmas-lights') {
          // No pricing - consultation required
          return null;
        }

        return null;
      };

      let minTotal = 0;
      let maxTotal = 0;

      data.services.forEach((serviceId) => {
        const pricing = calculateServicePrice(serviceId);
        if (pricing) {
          minTotal += pricing.min;
          maxTotal += pricing.max;
        }
      });

      const estimatedAmount = minTotal > 0 ? `$${minTotal} - $${maxTotal}` : 'Consultation required';

      // Map service IDs to names for email
      const services = data.services.map(serviceId => {
        const serviceMap: { [key: string]: string } = {
          'window': 'Window Cleaning',
          'gutter': 'Gutter Cleaning',
          'powerwashing': 'Power Washing',
          'roof': 'Roof Cleaning',
          'house-washing': 'House Washing',
          'christmas-lights': 'Christmas Lights'
        };
        return serviceMap[serviceId] || serviceId;
      });

      // Simple, reliable email delivery using Web3Forms (no API key needed)
      // This service sends form data directly to your email - 100% reliable
      const formData = new FormData();
      
      // Web3Forms access key (free, no signup needed for basic use)
      formData.append('access_key', '4ead49f0-be3d-4361-ade8-90c7624768e8'); // You'll get this from web3forms.com
      
      // Email recipient
      formData.append('subject', `New Quote Request from ${data.firstName} ${data.lastName}`);
      formData.append('from_name', 'Crystal Clean Quote System');
      formData.append('to_email', 'crystalcleanextr@gmail.com');
      
      // Customer information
      formData.append('customer_name', `${data.firstName} ${data.lastName}`);
      formData.append('customer_email', data.email);
      formData.append('customer_phone', data.phone);
      formData.append('customer_address', `${data.address}, ${data.city}, ${data.state} ${data.zipCode}`);
      
      // Property details
      formData.append('property_type', data.homeType);
      formData.append('property_size', data.homeSize);
      formData.append('property_stories', data.stories);
      
      // Services and pricing
      formData.append('services_requested', services.join(', '));
      formData.append('estimated_amount', estimatedAmount);
      
      // Additional details
      formData.append('service_details', JSON.stringify(data.serviceAnswers, null, 2));
      formData.append('additional_notes', data.additionalNotes || 'None');
      formData.append('promo_code', data.promoCode || 'None');
      formData.append('preferred_date', data.scheduledDate || 'Not specified');
      formData.append('preferred_time', data.scheduledTime || 'Not specified');

      // Send to Web3Forms API (simple POST request, no authentication needed)
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to send email');
      }

      console.log('Quote email sent successfully via Web3Forms');

      // Track successful quote submission
      analytics.trackQuoteSubmission(services, estimatedAmount);

      // Clear saved form data from local storage after successful submission
      try {
        localStorage.removeItem('crystal_clean_quote_form_data');
      } catch (error) {
        console.error('Error clearing saved form data:', error);
      }

      setSubmittedData(data);
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting quote:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setSubmissionError(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    setSubmissionError(null);
    // Form will be visible again for retry
  };

  // Error State UI
  if (submissionError) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-card rounded-lg shadow-brand p-8">
              {/* Error Icon */}
              <div className="flex items-center justify-center w-20 h-20 bg-destructive/10 rounded-full mx-auto mb-6">
                <Icon name="ExclamationTriangleIcon" size={48} className="text-destructive" />
              </div>

              {/* Error Title */}
              <h1 className="font-serif text-3xl font-semibold text-foreground mb-4 text-center">
                Submission Failed
              </h1>

              {/* Error Message */}
              <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4 mb-6">
                <p className="text-destructive text-center">
                  <strong>Error:</strong> {submissionError}
                </p>
              </div>

              {/* What to Do Next */}
              <div className="bg-surface rounded-lg p-6 mb-8">
                <h3 className="font-semibold text-foreground mb-4 text-center">What You Can Do</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      1
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-1">Try Again</h4>
                      <p className="text-sm text-muted-foreground">
                        Click the retry button below to resubmit your quote request
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      2
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-1">Call Us Directly</h4>
                      <p className="text-sm text-muted-foreground">
                        Speak with our team immediately at <strong className="text-foreground">(236) 867-2004</strong>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      3
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-1">Email Us</h4>
                      <p className="text-sm text-muted-foreground">
                        Send your request to <strong className="text-foreground">crystalcleanextr@gmail.com</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Options Highlight */}
              <div className="bg-accent border border-primary/20 rounded-lg p-6 mb-8">
                <h4 className="font-semibold text-foreground mb-4 text-center">
                  Prefer Direct Contact?
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Phone */}
                  <a
                    href="tel:2368672004"
                    className="flex items-center justify-center space-x-3 bg-white border border-border rounded-lg p-4 hover:border-primary hover:shadow-md transition-smooth"
                  >
                    <Icon name="PhoneIcon" size={24} className="text-primary" />
                    <div className="text-left">
                      <p className="text-xs text-muted-foreground">Call Us</p>
                      <p className="font-semibold text-foreground">(236) 867-2004</p>
                    </div>
                  </a>

                  {/* Email */}
                  <a
                    href="mailto:crystalcleanextr@gmail.com"
                    className="flex items-center justify-center space-x-3 bg-white border border-border rounded-lg p-4 hover:border-primary hover:shadow-md transition-smooth"
                  >
                    <Icon name="EnvelopeIcon" size={24} className="text-primary" />
                    <div className="text-left">
                      <p className="text-xs text-muted-foreground">Email Us</p>
                      <p className="font-semibold text-foreground text-sm">crystalcleanextr@gmail.com</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleRetry}
                  className="px-8 py-3 bg-primary text-white rounded-full font-semibold hover:bg-opacity-90 transition-smooth btn-bubbly flex items-center justify-center space-x-2"
                >
                  <Icon name="ArrowPathIcon" size={20} />
                  <span>Retry Submission</span>
                </button>
                <a
                  href="/homepage"
                  className="px-8 py-3 border border-border rounded-full font-medium text-foreground hover:bg-accent transition-smooth btn-bubbly text-center"
                >
                  Return to Homepage
                </a>
              </div>
            </div>

            {/* Additional Contact Options */}
            <div className="mt-8">
              <ContactOptions />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitted && submittedData) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-card rounded-lg shadow-brand p-8 text-center">
              <div className="flex items-center justify-center w-20 h-20 bg-success/10 rounded-full mx-auto mb-6">
                <Icon name="CheckCircleIcon" size={48} className="text-success" />
              </div>
              <h1 className="font-serif text-3xl font-semibold text-foreground mb-4">
                Quote Request Sent!
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Thank you, {submittedData.firstName}! Your free quote request has been sent directly to our team at Crystal Clean Exterior.
              </p>

              <div className="bg-surface rounded-lg p-6 mb-8 text-left">
                <h3 className="font-semibold text-foreground mb-4">What Happens Next?</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      1
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-1">Instant Delivery</h4>
                      <p className="text-sm text-muted-foreground">
Your quote request was sent instantly to kostamellios@gmail.com
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      2
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-1">Quick Response</h4>
                      <p className="text-sm text-muted-foreground">
                        We'll review your request and contact you within 24 hours
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      3
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-1">Personalized Quote</h4>
                      <p className="text-sm text-muted-foreground">
                        Receive your detailed quote with transparent pricing
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-accent border border-primary/20 rounded-lg p-6 mb-8">
                <div className="flex items-start space-x-3">
                  <Icon
                    name="InformationCircleIcon"
                    size={24}
                    className="text-primary flex-shrink-0 mt-1"
                  />
                  <div className="text-left">
                    <h4 className="font-semibold text-foreground mb-2">
                      Need Immediate Assistance?
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Call us directly at <strong>(236) 867-2004</strong> or email <strong>crystalcleanextr@gmail.com</strong>
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => {
                    setIsSubmitted(false);
                    setSubmittedData(null);
                  }}
                  className="px-6 py-3 border border-border rounded-full font-medium text-foreground hover:bg-accent transition-smooth btn-bubbly"
                >
                  Submit Another Request
                </button>
                <a
                  href="/homepage"
                  className="px-6 py-3 bg-primary text-white rounded-full font-semibold hover:bg-opacity-90 transition-smooth btn-bubbly"
                >
                  Return to Homepage
                </a>
              </div>
            </div>

            <div className="mt-8">
              <ContactOptions />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <QuoteFormSteps onSubmit={handleFormSubmit} />
      <QuoteTestimonials />
      <ContactOptions />
      <TrustIndicators />
    </div>
  );
};

export default GetQuoteInteractive;